import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import PropertyCard from '@/components/PropertyCard';
import api from '@/utils/api';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Search, MapPin, X } from 'lucide-react';

const Projects = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [properties, setProperties] = useState([]);
  const [allProperties, setAllProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    builder: searchParams.get('builder') || '',
    status: searchParams.get('status') || '',
    location: searchParams.get('location') || '',
    min_price: searchParams.get('min_price') || '',
    max_price: searchParams.get('max_price') || '',
  });
  const [builders, setBuilders] = useState([]);
  const [locations, setLocations] = useState([]);

  useEffect(() => {
    fetchBuilders();
    fetchAllProperties();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [filters, allProperties]);

  const fetchBuilders = async () => {
    try {
      const response = await api.get('/builders');
      setBuilders(response.data);
    } catch (error) {
      console.error('Error fetching builders:', error);
    }
  };

  const fetchAllProperties = async () => {
    setLoading(true);
    try {
      const response = await api.get('/properties');
      setAllProperties(response.data);
      
      // Extract unique locations from properties
      const uniqueLocations = [...new Set(response.data.map(p => {
        // Extract city/area from location (e.g., "Lower Parel, Mumbai" -> "Lower Parel")
        const parts = p.location.split(',');
        return parts[0].trim();
      }))].filter(Boolean).sort();
      setLocations(uniqueLocations);
    } catch (error) {
      console.error('Error fetching properties:', error);
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...allProperties];

    if (filters.builder) {
      filtered = filtered.filter(p => p.builder === filters.builder);
    }
    if (filters.status) {
      filtered = filtered.filter(p => p.status === filters.status);
    }
    if (filters.location) {
      filtered = filtered.filter(p => 
        p.location.toLowerCase().includes(filters.location.toLowerCase())
      );
    }
    if (filters.min_price) {
      filtered = filtered.filter(p => p.price >= parseFloat(filters.min_price));
    }
    if (filters.max_price) {
      filtered = filtered.filter(p => p.price <= parseFloat(filters.max_price));
    }

    setProperties(filtered);
  };

  const handleFilterChange = (key, value) => {
    const newFilters = { ...filters, [key]: value };
    setFilters(newFilters);

    const params = new URLSearchParams();
    Object.keys(newFilters).forEach((k) => {
      if (newFilters[k]) params.append(k, newFilters[k]);
    });
    setSearchParams(params);
  };

  const resetFilters = () => {
    setFilters({
      builder: '',
      status: '',
      location: '',
      min_price: '',
      max_price: '',
    });
    setSearchParams({});
  };

  const hasActiveFilters = Object.values(filters).some(v => v !== '');

  return (
    <div className="min-h-screen bg-dark" data-testid="projects-page">
      <Header />

      {/* Hero */}
      <section className="relative h-[400px] flex items-center justify-center overflow-hidden mt-32 bg-gradient-to-br from-dark via-dark-light to-dark">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0 bg-gradient-to-r from-rose-gold/20 to-transparent"></div>
        </div>
        <div className="relative z-10 text-center px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <p className="text-rose-gold text-sm mb-4 uppercase tracking-widest font-semibold">Premium Collection</p>
            <h1 className="font-heading text-6xl md:text-7xl font-bold text-white mb-4">
              Our <span className="text-rose-gold">Projects</span>
            </h1>
            <p className="text-lg text-white/70">
              Discover exceptional properties tailored to your lifestyle
            </p>
          </motion.div>
        </div>
      </section>

      {/* Filters */}
      <section className="py-12 px-6 md:px-12 lg:px-24 bg-dark-light/50 border-b border-dark-lighter" data-testid="property-filters">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-4">
          {/* Location Filter - NEW */}
          <div>
            <label className="text-sm font-semibold text-rose-gold mb-2 block uppercase tracking-wider flex items-center gap-2">
              <MapPin size={14} />
              Location
            </label>
            <Select
              value={filters.location}
              onValueChange={(value) => handleFilterChange('location', value === 'all' ? '' : value)}
            >
              <SelectTrigger data-testid="filter-location" className="bg-dark border-dark-lighter text-white rounded-lg focus:border-rose-gold">
                <SelectValue placeholder="All Locations" />
              </SelectTrigger>
              <SelectContent className="bg-dark-light border-dark-lighter text-white max-h-60">
                <SelectItem value="all">All Locations</SelectItem>
                {locations.map((location) => (
                  <SelectItem key={location} value={location}>
                    {location}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Builder Filter */}
          <div>
            <label className="text-sm font-semibold text-rose-gold mb-2 block uppercase tracking-wider">
              Builder
            </label>
            <Select
              value={filters.builder}
              onValueChange={(value) => handleFilterChange('builder', value === 'all' ? '' : value)}
            >
              <SelectTrigger data-testid="filter-builder" className="bg-dark border-dark-lighter text-white rounded-lg focus:border-rose-gold">
                <SelectValue placeholder="All Builders" />
              </SelectTrigger>
              <SelectContent className="bg-dark-light border-dark-lighter text-white">
                <SelectItem value="all">All Builders</SelectItem>
                {builders.map((builder) => (
                  <SelectItem key={builder.name} value={builder.name}>
                    {builder.name} ({builder.project_count})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Status Filter */}
          <div>
            <label className="text-sm font-semibold text-rose-gold mb-2 block uppercase tracking-wider">
              Status
            </label>
            <Select
              value={filters.status}
              onValueChange={(value) => handleFilterChange('status', value === 'all' ? '' : value)}
            >
              <SelectTrigger data-testid="filter-status" className="bg-dark border-dark-lighter text-white rounded-lg focus:border-rose-gold">
                <SelectValue placeholder="All Status" />
              </SelectTrigger>
              <SelectContent className="bg-dark-light border-dark-lighter text-white">
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="Available">Available</SelectItem>
                <SelectItem value="Sold">Sold Out</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Min Price Filter */}
          <div>
            <label className="text-sm font-semibold text-rose-gold mb-2 block uppercase tracking-wider">
              Min Price (₹)
            </label>
            <Input
              type="number"
              data-testid="filter-min-price"
              placeholder="Min"
              value={filters.min_price}
              onChange={(e) => handleFilterChange('min_price', e.target.value)}
              className="bg-dark border-dark-lighter text-white rounded-lg focus:border-rose-gold"
            />
          </div>

          {/* Max Price Filter */}
          <div>
            <label className="text-sm font-semibold text-rose-gold mb-2 block uppercase tracking-wider">
              Max Price (₹)
            </label>
            <Input
              type="number"
              data-testid="filter-max-price"
              placeholder="Max"
              value={filters.max_price}
              onChange={(e) => handleFilterChange('max_price', e.target.value)}
              className="bg-dark border-dark-lighter text-white rounded-lg focus:border-rose-gold"
            />
          </div>

          {/* Reset Button */}
          <div className="flex items-end">
            <Button
              data-testid="reset-filters-button"
              onClick={resetFilters}
              disabled={!hasActiveFilters}
              className={`w-full text-sm uppercase tracking-wider ${
                hasActiveFilters 
                  ? 'btn-outline' 
                  : 'bg-dark-lighter text-white/40 cursor-not-allowed'
              }`}
            >
              <X size={16} className="mr-2" />
              Reset
            </Button>
          </div>
        </div>

        {/* Active Filters Display */}
        {hasActiveFilters && (
          <div className="mt-4 flex flex-wrap gap-2">
            {filters.location && (
              <span className="inline-flex items-center gap-1 bg-rose-gold/20 text-rose-gold px-3 py-1 rounded-full text-sm">
                <MapPin size={12} />
                {filters.location}
                <button onClick={() => handleFilterChange('location', '')} className="ml-1 hover:text-white">
                  <X size={14} />
                </button>
              </span>
            )}
            {filters.builder && (
              <span className="inline-flex items-center gap-1 bg-rose-gold/20 text-rose-gold px-3 py-1 rounded-full text-sm">
                {filters.builder}
                <button onClick={() => handleFilterChange('builder', '')} className="ml-1 hover:text-white">
                  <X size={14} />
                </button>
              </span>
            )}
            {filters.status && (
              <span className="inline-flex items-center gap-1 bg-rose-gold/20 text-rose-gold px-3 py-1 rounded-full text-sm">
                {filters.status}
                <button onClick={() => handleFilterChange('status', '')} className="ml-1 hover:text-white">
                  <X size={14} />
                </button>
              </span>
            )}
          </div>
        )}
      </section>

      {/* Results Count */}
      <div className="px-6 md:px-12 lg:px-24 py-4 bg-dark border-b border-dark-lighter">
        <p className="text-white/60 text-sm">
          Showing <span className="text-rose-gold font-semibold">{properties.length}</span> projects
          {hasActiveFilters && <span> (filtered)</span>}
        </p>
      </div>

      {/* Properties Grid */}
      <section className="py-20 px-6 md:px-12 lg:px-24 bg-dark">
        {loading ? (
          <div className="text-center text-white/60" data-testid="loading-properties">
            Loading projects...
          </div>
        ) : properties.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-10" data-testid="properties-grid">
            {properties.map((property) => (
              <PropertyCard key={property.id} property={property} />
            ))}
          </div>
        ) : (
          <div className="text-center py-20" data-testid="no-properties-message">
            <p className="text-white/60 text-lg mb-4">No projects found matching your criteria.</p>
            <Button
              onClick={resetFilters}
              className="btn-primary text-sm uppercase tracking-wider"
            >
              Clear Filters
            </Button>
          </div>
        )}
      </section>

      <Footer />
    </div>
  );
};

export default Projects;
